// pages/Explore.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiMapPin, 
  FiSearch, 
  FiCalendar, 
  FiFilter,
  FiMap,
  FiGrid,
  FiUsers,
  FiX,
  FiNavigation
} from 'react-icons/fi';
import { GetCountries, GetState, GetCity } from 'react-country-state-city';
import DashboardLayout from '../components/DashboardLayout';
import LocationSelectorModal from '../components/LocationSelectorModal';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import '../styles/Explore.scss';

const CATEGORIES = [
  { id: 'all', name: 'All', icon: '🎉' },
  { id: 'party', name: 'Party', icon: '🎵' },
  { id: 'concert', name: 'Concert', icon: '🎤' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
  { id: 'food', name: 'Food', icon: '🍽️' },
  { id: 'networking', name: 'Network', icon: '👥' },
  { id: 'workshop', name: 'Workshop', icon: '📚' },
  { id: 'other', name: 'Other', icon: '✨' },
];

// Helper function to get location IDs
const getLocationIds = async (country, state, city) => {
  try {
    const countries = await GetCountries();
    const countryData = countries.find(c => c.name === country);
    
    if (!countryData) return { countryId: null, stateId: null, cityId: null };

    const states = await GetState(countryData.id);
    const stateData = states.find(s => s.name === state);
    
    if (!stateData) return { countryId: countryData.id, stateId: null, cityId: null };

    const cities = await GetCity(countryData.id, stateData.id);
    const cityData = cities.find(c => c.name === city);
    
    return {
      countryId: countryData.id,
      stateId: stateData.id,
      cityId: cityData?.id || null,
    };
  } catch (error) {
    console.error('Error getting location IDs:', error);
    return { countryId: null, stateId: null, cityId: null };
  }
};

// Helper function to get Mumbai with IDs
const getMumbaiLocationWithIds = async () => {
  const ids = await getLocationIds('India', 'Maharashtra', 'Mumbai');
  return {
    city: 'Mumbai',
    state: 'Maharashtra',
    country: 'India',
    ...ids,
  };
};

const Explore = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Location state with IDs
  const [selectedLocation, setSelectedLocation] = useState({
    city: 'Mumbai',
    state: 'Maharashtra',
    country: 'India',
    cityId: null,
    stateId: null,
    countryId: null,
  });
  
  const [locationLoading, setLocationLoading] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationPickerType, setLocationPickerType] = useState('city');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedDate, setSelectedDate] = useState(null);
  
  const [allInvitations, setAllInvitations] = useState([]);
  const [stateInvitations, setStateInvitations] = useState([]);
  const [featuredInvitations, setFeaturedInvitations] = useState([]);
  const [userHostedInvitations, setUserHostedInvitations] = useState([]);
  const [vibesInvitations, setVibesInvitations] = useState([]);
  const [communityInvitations, setCommunityInvitations] = useState([]);

  const [loading, setLoading] = useState(true);

  // Initialize location with IDs on mount
  useEffect(() => {
    initializeLocation();
  }, []);

  // Fetch data when location changes (but only after IDs are loaded)
  useEffect(() => {
    if (!locationLoading && selectedLocation.countryId && selectedLocation.stateId) {
      fetchAllData();
    }
  }, [selectedLocation, selectedCategory, selectedDate, locationLoading]);

  const initializeLocation = async () => {
    try {
      setLocationLoading(true);
      console.log('🔄 Initializing location...');
      
      // Try to load cached location first
      const cachedLocation = localStorage.getItem('explore_location');
      if (cachedLocation) {
        try {
          const parsed = JSON.parse(cachedLocation);
          console.log('✅ Loaded cached location:', parsed);
          setSelectedLocation(parsed);
          setLocationLoading(false);
          return;
        } catch (e) {
          console.error('❌ Error parsing cached location:', e);
          localStorage.removeItem('explore_location');
        }
      }
      
      // No cached location, use Mumbai as default
      console.log('🔄 No cached location, loading Mumbai...');
      const mumbaiLocation = await getMumbaiLocationWithIds();
      console.log('✅ Mumbai location loaded:', mumbaiLocation);
      setSelectedLocation(mumbaiLocation);
      // Cache the default location
      localStorage.setItem('explore_location', JSON.stringify(mumbaiLocation));
    } catch (error) {
      console.error('❌ Error initializing location:', error);
      // Fallback without IDs
      const fallbackLocation = {
        city: 'Mumbai',
        state: 'Maharashtra',
        country: 'India',
        cityId: null,
        stateId: null,
        countryId: null,
      };
      setSelectedLocation(fallbackLocation);
      localStorage.setItem('explore_location', JSON.stringify(fallbackLocation));
    } finally {
      setLocationLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch all published events
      const { data: allEvents, error } = await supabase
        .from('events')
        .select(`
          *,
          host:users!host_id(
            id,
            first_name,
            last_name,
            profile_photo_cdn_url
          ),
          community:communities(
            id,
            name,
            username
          )
        `)
        .eq('status', 'published')
        .order('event_date', { ascending: true })
        .limit(200);

      if (error) throw error;

      // Filter events by privacy settings
      const filteredEvents = (allEvents || []).filter(event => {
        return (
          event.privacy === 'public' ||
          event.privacy === 'approval' ||
          (event.privacy === 'private' && event.show_in_explore_feed === true)
        );
      });

      // Apply category filter
      let categoryFiltered = filteredEvents;
      if (selectedCategory !== 'all') {
        categoryFiltered = filteredEvents.filter(event => event.event_type === selectedCategory);
      }

      // Apply date filter
      let dateFiltered = categoryFiltered;
      if (selectedDate) {
        const dateStr = selectedDate.toISOString().split('T')[0];
        dateFiltered = categoryFiltered.filter(event => {
          const eventDate = new Date(event.event_date).toISOString().split('T')[0];
          return eventDate === dateStr;
        });
      }

      // City events - events in the selected city
      const cityEvents = dateFiltered.filter(event =>
        event.location_city === selectedLocation.city
      );
      setAllInvitations(cityEvents);

      // State events - events from same state but different city
      const stateEvents = dateFiltered.filter(event =>
        event.location_state === selectedLocation.state &&
        event.location_city !== selectedLocation.city
      );
      setStateInvitations(stateEvents);

      // User hosted events (if logged in)
      if (user) {
        const userHosted = cityEvents.filter(event => event.host_id === user.id);
        setUserHostedInvitations(userHosted);

        // Vibes - events user is attending
        const { data: userAttendance } = await supabase
          .from('event_attendees')
          .select('event_id')
          .eq('user_id', user.id)
          .eq('status', 'accepted');

        const attendingEventIds = new Set((userAttendance || []).map(a => a.event_id));
        const vibes = cityEvents.filter(event => attendingEventIds.has(event.id));
        setVibesInvitations(vibes);

        // Community events - events from communities user is part of
        const { data: userCommunities } = await supabase
          .from('community_members')
          .select('community_id')
          .eq('user_id', user.id)
          .eq('status', 'active');

        const communityIds = new Set((userCommunities || []).map(c => c.community_id));
        const communityEvents = cityEvents.filter(event =>
          event.community_id && communityIds.has(event.community_id)
        );
        setCommunityInvitations(communityEvents);
      }

      // Featured events (most popular in city)
      const featured = [...cityEvents].sort((a, b) =>
        (b.attendee_count || 0) - (a.attendee_count || 0)
      ).slice(0, 5);
      setFeaturedInvitations(featured);

    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = async (locationData) => {
    let newLocation;
    
    if (locationData.city) {
      // Complete location data
      newLocation = {
        city: locationData.city,
        state: locationData.state || selectedLocation.state,
        country: locationData.country || selectedLocation.country,
        cityId: locationData.cityId,
        stateId: locationData.stateId || selectedLocation.stateId,
        countryId: locationData.countryId || selectedLocation.countryId,
      };
    } else if (locationData.state) {
      // Changed state, reset city
      newLocation = {
        city: null,
        state: locationData.state,
        country: locationData.country || selectedLocation.country,
        cityId: null,
        stateId: locationData.stateId,
        countryId: locationData.countryId || selectedLocation.countryId,
      };
      // Stay in modal to select city
      setLocationPickerType('city');
      setSelectedLocation(newLocation);
      return;
    } else if (locationData.country) {
      // Changed country, reset state and city
      newLocation = {
        city: null,
        state: null,
        country: locationData.country,
        cityId: null,
        stateId: null,
        countryId: locationData.countryId,
      };
      // Stay in modal to select state
      setLocationPickerType('state');
      setSelectedLocation(newLocation);
      return;
    }
    
    setSelectedLocation(newLocation);
    // Cache the selected location
    localStorage.setItem('explore_location', JSON.stringify(newLocation));
    setShowLocationModal(false);
    setLocationPickerType('city'); // Reset to city for next time
  };

  const handleNavigateToState = () => {
    setLocationPickerType('state');
  };

  const handleNavigateToCountry = () => {
    setLocationPickerType('country');
  };

  const handleOpenLocationModal = () => {
    setLocationPickerType('city'); // Always start with city list
    setShowLocationModal(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search logic
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderEventCard = (event) => (
    <motion.div
      key={event.id}
      className="event-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={() => navigate(`/i/${event.shareable_slug}`)}
    >
      <div className="event-image">
        {event.cover_image_cdn_url || event.gif_cover_cdn_url ? (
          <img 
            src={event.cover_image_cdn_url || event.gif_cover_cdn_url} 
            alt={event.title} 
          />
        ) : (
          <div className="event-image-placeholder">
            <FiCalendar size={32} />
          </div>
        )}
        {event.cover_image_type === 'gif' && (
          <div className="gif-badge">
            <span>GIF</span>
          </div>
        )}
        <div className="event-overlay"></div>
      </div>
      
      <div className="event-content">
        <h3 className="event-title">{event.title}</h3>
        
        <div className="event-meta">
          <div className="meta-item">
            <FiCalendar size={12} />
            <span>{formatDate(event.event_date)}</span>
          </div>
          {event.location_name && (
            <div className="meta-item">
              <FiMapPin size={12} />
              <span>{event.location_name}</span>
            </div>
          )}
        </div>

        {event.attendee_count > 0 && (
          <div className="event-attendance">
            <FiUsers size={14} />
            <span>{event.attendee_count} going</span>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderHorizontalCard = (event) => (
    <motion.div
      key={event.id}
      className="horizontal-event-card"
      whileHover={{ x: -4 }}
      onClick={() => navigate(`/i/${event.shareable_slug}`)}
    >
      <div className="event-image">
        {event.cover_image_cdn_url || event.gif_cover_cdn_url ? (
          <img 
            src={event.cover_image_cdn_url || event.gif_cover_cdn_url} 
            alt={event.title} 
          />
        ) : (
          <div className="event-image-placeholder">
            <FiCalendar size={24} />
          </div>
        )}
        <div className="event-overlay"></div>
      </div>
      
      <div className="event-content">
        <h4 className="event-title">{event.title}</h4>
        <div className="event-meta">
          <div className="meta-item">
            <FiCalendar size={12} />
            <span>{formatDate(event.event_date)}</span>
          </div>
          {event.attendee_count > 0 && (
            <div className="meta-item">
              <FiUsers size={12} />
              <span>{event.attendee_count} going</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );

  if (locationLoading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading location...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="explore-page">
        {/* Header Section */}
        <div className="explore-header">
          <motion.div 
            className="header-content glass-card"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Location & View Toggle */}
            <div className="header-top">
              <button 
                className="location-btn"
                onClick={handleOpenLocationModal}
              >
                <FiMapPin />
                <span>{selectedLocation.city || 'Select Location'}</span>
              </button>
              
              <div className="view-controls">
                <button 
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  <FiGrid />
                </button>
                <button 
                  className={`view-btn ${viewMode === 'map' ? 'active' : ''}`}
                  onClick={() => setViewMode('map')}
                >
                  <FiMap />
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <form className="search-container" onSubmit={handleSearch}>
              <FiSearch />
              <input
                type="text"
                placeholder="Search invitations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button 
                  type="button"
                  className="clear-btn"
                  onClick={() => setSearchQuery('')}
                >
                  <FiX />
                </button>
              )}
            </form>

            {/* Categories */}
            <div className="categories-scroll">
              {CATEGORIES.map(category => (
                <button
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <span className="category-icon">{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Content */}
        <div className="explore-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading invitations...</p>
            </div>
          ) : (
            <>
              {/* User Hosted Events */}
              {user && userHostedInvitations.length > 0 && (
                <motion.div
                  className="events-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="section-header">
                    <h2>🎯 Your Hosted Events</h2>
                  </div>
                  <div className="horizontal-scroll">
                    {userHostedInvitations.map(renderHorizontalCard)}
                  </div>
                </motion.div>
              )}

              {/* Vibes - Events User is Attending */}
              {user && vibesInvitations.length > 0 && (
                <motion.div
                  className="events-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="section-header">
                    <h2>✨ Your Vibes</h2>
                  </div>
                  <div className="horizontal-scroll">
                    {vibesInvitations.map(renderHorizontalCard)}
                  </div>
                </motion.div>
              )}

              {/* Community Events */}
              {user && communityInvitations.length > 0 && (
                <motion.div
                  className="events-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="section-header">
                    <h2>👥 From Your Communities</h2>
                  </div>
                  <div className="horizontal-scroll">
                    {communityInvitations.map(renderHorizontalCard)}
                  </div>
                </motion.div>
              )}

              {/* Featured Section */}
              {featuredInvitations.length > 0 && (
                <motion.div
                  className="events-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="section-header">
                    <h2>🔥 Trending in {selectedLocation.city}</h2>
                  </div>
                  <div className="horizontal-scroll">
                    {featuredInvitations.map(renderHorizontalCard)}
                  </div>
                </motion.div>
              )}

              {/* City Invitations */}
              <motion.div
                className="events-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="section-header">
                  <h2>
                    {selectedCategory === 'all'
                      ? `Invitations in ${selectedLocation.city}`
                      : `${CATEGORIES.find(c => c.id === selectedCategory)?.name} in ${selectedLocation.city}`
                    }
                  </h2>
                </div>

                {allInvitations.length > 0 ? (
                  <div className="events-grid">
                    {allInvitations.map(renderEventCard)}
                  </div>
                ) : (
                  <div className="empty-state glass-card">
                    <FiCalendar size={48} />
                    <h3>No invitations found in {selectedLocation.city}</h3>
                    <p>
                      {selectedCategory !== 'all'
                        ? 'Try a different category'
                        : 'Check back later for new events'
                      }
                    </p>
                  </div>
                )}
              </motion.div>

              {/* State Invitations */}
              {stateInvitations.length > 0 && (
                <motion.div
                  className="events-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="section-header">
                    <h2>📍 More in {selectedLocation.state}</h2>
                  </div>

                  <div className="events-grid">
                    {stateInvitations.slice(0, 6).map(renderEventCard)}
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Location Selector Modal */}
      <LocationSelectorModal
        visible={showLocationModal}
        onClose={() => {
          setShowLocationModal(false);
          setLocationPickerType('city'); // Reset to city
        }}
        onSelect={handleLocationChange}
        type={locationPickerType}
        countryId={selectedLocation.countryId}
        stateId={selectedLocation.stateId}
        currentValue={
          locationPickerType === 'country' 
            ? selectedLocation.country
            : locationPickerType === 'state'
              ? selectedLocation.state
              : selectedLocation.city
        }
        onNavigateToState={locationPickerType === 'city' ? handleNavigateToState : null}
        onNavigateToCountry={locationPickerType === 'state' ? handleNavigateToCountry : null}
      />
    </DashboardLayout>
  );
};

export default Explore;